/**
 * ChatHeader Component - Discussion Overlay Header
 *
 * Displays:
 * - Trip name or "Discussion" title
 * - Clickable "Discussion Group" subtitle (opens members dialog)
 * - Participant count or avatars (Phase 2)
 * - Close button (X icon)
 *
 * Features:
 * - Sticky positioning when scrolling
 * - Responsive button sizing
 * - Icon-based actions
 * - Members dialog integration
 */

import React, { useState } from 'react';
import { Box, Typography, IconButton, Tooltip } from '@mui/material';
import {
  ChatBubble as ChatBubbleIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import MembersDialog from './MembersDialog';

/**
 * ChatHeader Component
 *
 * @param {Object} props
 * @param {string} [props.tripName] - Name of the trip (optional)
 * @param {Object} [props.trip] - Full trip object for members dialog
 * @param {string} [props.ownerEmail] - Owner's email address
 * @param {string} [props.currentUserEmail] - Current user's email address
 * @param {boolean} [props.isSharedTrip] - Whether this is a shared trip
 * @param {number} [props.participantCount=0] - Number of participants
 * @param {Array} [props.participants] - Participant data (Phase 2)
 * @param {Function} props.onMinimize - Callback to minimize/close overlay
 */
const ChatHeader = ({
  tripName,
  trip,
  ownerEmail,
  currentUserEmail,
  isSharedTrip,
  participantCount = 0,
  participants = [],
  onMinimize
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [membersDialogOpen, setMembersDialogOpen] = useState(false);

  const handleOpenMembers = () => {
    console.log('[ChatHeader] Opening members dialog', {
      trip: trip,
      ownerEmail: ownerEmail,
      currentUserEmail: currentUserEmail,
      isSharedTrip: isSharedTrip
    });
    setMembersDialogOpen(true);
  };

  const handleCloseMembers = () => {
    setMembersDialogOpen(false);
  };

  console.log('[ChatHeader] Rendering with props:', {
    tripName,
    hasTripObject: !!trip,
    ownerEmail,
    currentUserEmail,
    isSharedTrip
  });

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '12px 16px',
        borderBottom: '1px solid',
        borderColor: 'divider',
        backgroundColor: 'background.paper',
        position: 'sticky',
        top: 0,
        zIndex: 10,
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)'
      }}
    >
      {/* Left: Title */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          flex: 1,
          minWidth: 0 // Allow text truncation
        }}
      >
        <ChatBubbleIcon
          sx={{
            color: 'primary.main',
            fontSize: '1.25rem'
          }}
        />
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="h6"
            sx={{
              fontSize: '1rem',
              fontWeight: 600,
              color: 'text.primary',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
          >
            {tripName || 'Discussion'}
          </Typography>
          <Typography
            variant="caption"
            onClick={handleOpenMembers}
            sx={{
              color: 'primary.main',
              fontSize: '0.75rem',
              display: 'block',
              cursor: 'pointer',
              textDecoration: 'underline',
              '&:hover': {
                color: 'primary.dark'
              }
            }}
          >
            Discussion Group
          </Typography>
          {participantCount > 0 && (
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                fontSize: '0.75rem',
                display: 'block'
              }}
            >
              {participantCount} {participantCount === 1 ? 'participant' : 'participants'}
            </Typography>
          )}
        </Box>
      </Box>

      {/* Right: Actions */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5
        }}
      >
        {/* Future: Participant Avatars (Phase 2) */}
        {/* <AvatarGroup max={3}>
          {participants.map(participant => (
            <Avatar key={participant.id} src={participant.avatar} />
          ))}
        </AvatarGroup> */}

        {/* Close Button */}
        <Tooltip title="Close">
          <IconButton
            onClick={onMinimize}
            size="small"
            sx={{
              color: 'text.secondary',
              '&:hover': {
                backgroundColor: 'action.hover'
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Members Dialog */}
      <MembersDialog
        open={membersDialogOpen}
        onClose={handleCloseMembers}
        trip={trip}
        ownerEmail={ownerEmail}
        currentUserEmail={currentUserEmail}
        isSharedTrip={isSharedTrip}
      />
    </Box>
  );
};

export default ChatHeader;
